import axios from 'axios';
import { axiosETAGCache } from 'axios-etag-cache';

let globalTenantConfigurationSchema = null;

// Apply the axios ETAG interceptor
const axiosWithETAGCache = axiosETAGCache(axios);
const api = {
  async getTenantConfigurationSchema() {
    if (globalTenantConfigurationSchema) {
      return globalTenantConfigurationSchema;
    }
    const response = await axiosWithETAGCache.get(
      `/tenant_configuration/schema`
    );
    globalTenantConfigurationSchema = response.data;
    return response.data;
  },
  async getTenantConfigurations(tableQuery) {
    const queryParams = {
      query: JSON.stringify(tableQuery),
    };

    const response = await axiosWithETAGCache.get(`/tenant_configuration`, {
      params: queryParams,
    });

    return response.data;
  },
  async createNewTenantConfiguration(configData) {
    const response = await axios.post(`/tenant_configuration`, configData);

    return response.data;
  },
  async getTenantConfiguration(tenantConfigurationId) {
    const queryParams = {};

    const response = await axiosWithETAGCache.get(
      `/tenant_configuration/${tenantConfigurationId}`,
      {
        params: queryParams,
      }
    );

    return response.data;
  },
  async saveTenantConfiguration(tenantConfiguration) {
    const response = await axios.put(
      `/tenant_configuration/${tenantConfiguration.id}`,
      tenantConfiguration
    );
    return response.data;
  },
  async deleteTenantConfiguration(tenantConfigurationId) {
    const response = await axios.delete(
      `/tenant_configuration/${tenantConfigurationId}`
    );

    return response.data;
  },
  // --- tenant deletion -------------------------------------------------------
  //
  // 🔴 Every one of these uses BARE `axios`, never `axiosWithETAGCache`.
  //
  // The preview is the last thing an admin reads before an irreversible action; a
  // 304 would show them a cached blast radius for a tenant whose contents have since
  // changed. And a cached run status would hand back a stale record forever, so the
  // progress view would never advance past "running". The mutating methods above
  // already use bare axios, so this follows the existing convention.
  async getTenantDeletionImpact(tenantConfigurationId) {
    const response = await axios.get(
      `/tenant_configuration/${tenantConfigurationId}/deletion_preview`
    );
    return response.data;
  },
  async startTenantDeletion(tenantConfigurationId, options = {}) {
    const response = await axios.post(
      `/tenant_configuration/${tenantConfigurationId}/deletion_runs`,
      {
        // The server checks this too -- validation where it is displayed is not
        // enforcement, and this endpoint is reachable with curl.
        confirm_tenant_id: options.confirmTenantId,
        purge_now: Boolean(options.purgeNow),
        // Only meaningful when purgeNow is set; the server requires it separately
        // because skipping the 7-day hold cannot be undone.
        confirm_no_recovery_window: options.confirmNoRecoveryWindow,
      }
    );
    return response.data;
  },
  async getTenantDeletionRuns(tenantConfigurationId) {
    const response = await axios.get(
      `/tenant_configuration/${tenantConfigurationId}/deletion_runs`
    );
    return response.data;
  },
  async getTenantDeletionRun(tenantConfigurationId, runId) {
    const response = await axios.get(
      `/tenant_configuration/${tenantConfigurationId}/deletion_runs/${runId}`
    );
    return response.data;
  },
  async cancelTenantDeletionRun(tenantConfigurationId, runId) {
    const response = await axios.delete(
      `/tenant_configuration/${tenantConfigurationId}/deletion_runs/${runId}`
    );
    return response.data;
  },
  async getTenantProviderCosts(tenantConfigurationId, params = {}) {
    const response = await axiosWithETAGCache.get(
      `/tenant_configuration/${tenantConfigurationId}/provider_costs`,
      {
        params,
      }
    );

    return response.data;
  },
  async importTenantTwilioProviderCosts(tenantConfigurationId, payload) {
    const response = await axios.post(
      `/tenant_configuration/${tenantConfigurationId}/provider_costs/import/twilio`,
      payload
    );

    return response.data;
  },
  async importTenantProviderCosts(tenantConfigurationId, provider, payload) {
    const response = await axios.post(
      `/tenant_configuration/${tenantConfigurationId}/provider_costs/import/${provider}`,
      payload
    );

    return response.data;
  },
  async getTenantProviderCostImportRuns(tenantConfigurationId) {
    const response = await axiosWithETAGCache.get(
      `/tenant_configuration/${tenantConfigurationId}/provider_costs/import_runs`
    );

    return response.data;
  },
};

export default api;
